import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

/**
 * 에디터가 **포커스를 잃었을 때도 텍스트 선택 영역을 시각적으로 유지**하는 확장.
 *
 * 브라우저는 contenteditable이 blur되면 네이티브 선택 하이라이트를 그리지 않는다.
 * 그래서 텍스트를 범위 선택한 뒤 포매팅 툴바의 드롭다운(글자 크기 등, 포커스를
 * 가져감)을 열면 선택 하이라이트가 사라져 어디에 적용되는지 안 보였다.
 * (ProseMirror 선택 state는 유지되므로 글자 크기 적용 자체는 정상)
 *
 * 이 플러그인은 에디터가 blur 상태일 때 현재 TextSelection 범위에 인라인
 * 데코레이션(`.bn-inactive-selection`)을 입혀 선택을 계속 보이게 한다.
 * 포커스가 있을 때는 데코레이션을 비워 네이티브 선택을 그대로 사용한다.
 */
const inactiveSelectionKey = new PluginKey<boolean>("lumirInactiveSelection");

export const InactiveSelectionExtension = Extension.create({
  name: "lumirInactiveSelection",

  addProseMirrorPlugins() {
    return [
      new Plugin<boolean>({
        key: inactiveSelectionKey,
        // 플러그인 state = 에디터 포커스 여부
        state: {
          init: () => false,
          apply: (tr, value) => {
            const meta = tr.getMeta(inactiveSelectionKey);
            return typeof meta === "boolean" ? meta : value;
          },
        },
        props: {
          decorations(state) {
            const hasFocus = inactiveSelectionKey.getState(state);
            if (hasFocus) {
              return null; // 포커스 있으면 네이티브 선택 사용
            }
            const { selection } = state;
            // 비어있는 커서/비텍스트 선택은 제외(테이블 셀 등은 자체 표시)
            if (selection.empty || !(selection instanceof TextSelection)) {
              return null;
            }
            return DecorationSet.create(state.doc, [
              Decoration.inline(selection.from, selection.to, {
                class: "bn-inactive-selection",
              }),
            ]);
          },
        },
        view(view) {
          const sync = () => {
            const has = view.hasFocus();
            if (inactiveSelectionKey.getState(view.state) !== has) {
              view.dispatch(view.state.tr.setMeta(inactiveSelectionKey, has));
            }
          };
          view.dom.addEventListener("focus", sync);
          view.dom.addEventListener("blur", sync);
          // 초기 상태 반영
          const raf = requestAnimationFrame(sync);
          return {
            destroy() {
              cancelAnimationFrame(raf);
              view.dom.removeEventListener("focus", sync);
              view.dom.removeEventListener("blur", sync);
            },
          };
        },
      }),
    ];
  },
});
